import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faFolderOpen,
  faSave,
} from "@fortawesome/free-solid-svg-icons";
import { useAdminNews } from "../../../store/admin";
import {useConfirmDialog} from "../../../components/ConfirmDialogProvider";

// News category manager — GET/POST/PATCH/DELETE /api/admin/news/categories
const EMPTY_FORM = { name: "", description: "" };

const NewsCategoriesModal = ({ isOpen, onClose, onSuccess, onError, useNewsStore = useAdminNews }) => {
  const confirm = useConfirmDialog();
  // All category calls live in the admin store.
  const {
    newsCategories: categories,
    newsCategoriesLoading: loading,
    fetchNewsCategories,
    createNewsCategory,
    updateNewsCategory,
    deleteNewsCategory,
  } = useNewsStore();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchNewsCategories();
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      setDeletingId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const startCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setForm({ name: cat.name || "", description: cat.description || "" });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = { name: form.name, description: form.description };
    const res = editingId
      ? await updateNewsCategory(editingId, payload)
      : await createNewsCategory(payload);
    if (res.success) {
      onSuccess(editingId ? "Category updated" : "Category created");
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      await fetchNewsCategories();
    } else {
      onError(res.error || "Failed to save category", false);
    }
    setSubmitting(false);
  };

  const handleDelete = async (cat) => {
    return confirm({
      title: `Delete the "${cat.name}" category?`,
      description: "This action cannot be undone.",
      confirmLabel: "Delete category",
      onConfirm: async () => {
        setDeletingId(cat.id);
        try {
          const res = await deleteNewsCategory(cat.id);
          if (!res.success) throw new Error(res.error || "Failed to delete category.");
          onSuccess("Category deleted");
          await fetchNewsCategories();
        } catch (error) {
          onError(error.message || "Failed to delete category", false);
          throw error;
        } finally {
          setDeletingId(null);
        }
      },
    });
  };

  return (
    <Modal show={isOpen} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faFolderOpen} className="me-2 text-indigo-500" />
          News Categories
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {/* Header row */}
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm text-slate-500">{categories.length} categor{categories.length === 1 ? "y" : "ies"}</span>
          {!showForm && (
            <Button variant="outline-primary" size="sm" onClick={startCreate}>
              <FontAwesomeIcon icon={faPlus} className="me-1" /> Add Category
            </Button>
          )}
        </div>

        {/* Create/Edit form */}
        {showForm && (
          <Form onSubmit={handleSubmit} className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <h6 className="mb-2 text-sm font-semibold text-slate-700">
              {editingId ? "Edit Category" : "New Category"}
            </h6>
            <Form.Group className="mb-2">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Education"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Education-related news and updates"
              />
            </Form.Group>
            <div className="d-flex gap-2">
              <Button variant="primary" type="submit" size="sm" disabled={submitting}>
                <FontAwesomeIcon icon={faSave} className="me-1" />
                {submitting ? "Saving..." : editingId ? "Save Changes" : "Create"}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setForm(EMPTY_FORM);
                }}
              >
                Cancel
              </Button>
            </div>
          </Form>
        )}

        {/* Category list */}
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border text-primary" role="status" />
          </div>
        ) : categories.length === 0 ? (
          <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center">
            <FontAwesomeIcon icon={faFolderOpen} className="mb-2 text-4xl text-slate-300" />
            <p className="mb-0 text-slate-500">No categories yet. Create your first one.</p>
          </div>
        ) : (
          <ul className="list-group">
            {categories.map((cat) => (
              <li
                key={cat.id}
                className="list-group-item d-flex align-items-center gap-3"
              >
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle text-white fw-bold"
                  style={{ width: 40, height: 40, backgroundColor: "#4f46e5" }}
                >
                  {(cat.name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-grow-1">
                  <div className="fw-semibold text-slate-800">{cat.name}</div>
                  {cat.description && (
                    <div className="text-muted small">{cat.description}</div>
                  )}
                </div>
                <div className="d-flex gap-1">
                  <Button variant="outline-secondary" size="sm" onClick={() => startEdit(cat)}>
                    <FontAwesomeIcon icon={faEdit} />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    disabled={deletingId === cat.id}
                    onClick={() => handleDelete(cat)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default NewsCategoriesModal;
